import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da20np5v7gurs51ef2l0";

const inputCls =
  "w-full rounded-md border border-background-300/70 bg-background-100 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 transition-colors focus:border-primary-500 focus:outline-none";

export default function Contact() {
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setFormError(null);
    setSubmitting(true);

    const data = new FormData(form);
    const honeypot = ((data.get("website_alt") as string) || "").trim();
    if (honeypot) {
      // 蜜罐命中：给出与正常提交一致的成功反馈，不向表单地址发送数据
      setFormSuccess(true);
      form.reset();
      setSubmitting(false);
      return;
    }
    data.delete("website_alt");

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        (parsed?.meta?.message as string) ||
        (parsed?.message as string) ||
        (parsed?.meta?.detail as string) ||
        responseText;
      const textLower = String(serverMsg || "").toLowerCase();
      if (res.ok && parsed?.code === "OK" && !textLower.includes("spam")) {
        setFormSuccess(true);
        form.reset();
      } else {
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim() ? serverMsg : "提交失败，请稍后再试。"
        );
      }
    } catch {
      setFormError("网络异常，请稍后再试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <Seo
        title="联系我们 - 吹雪漫画网"
        description="联系吹雪漫画网：反馈阅读问题、建议收录题材或洽谈合作。我们会在 1-3 个工作日内回复每一封来信，让每一份关于冬日漫画的建议都被认真对待。"
        keywords="吹雪漫画网,联系我们,投稿,合作,反馈,雪景漫画"
        canonical="https://secretom.github.io/contact"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "联系吹雪漫画网",
            url: "https://secretom.github.io/contact",
          },
        ]}
      />

      <section className="relative flex min-h-[280px] w-full items-center overflow-hidden pt-24 md:min-h-[330px]">
        <div className="absolute inset-0 bg-background-100"></div>
        <div className="absolute -left-20 top-8 h-64 w-64 rounded-full bg-primary-500/10 blur-3xl"></div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 text-center md:px-6">
          <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-primary-500/40 bg-primary-500/10 px-4 py-1.5 text-sm text-primary-500">
            <i className="ri-mail-line"></i> 联系我们
          </span>
          <h1 className="mt-5 font-heading text-3xl font-black text-foreground-950 md:text-5xl">
            你的每一条建议，都是一场雪
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-7 text-foreground-600 md:text-base">
            无论是反馈阅读问题、建议补充题材，还是洽谈合作，都可以通过下方表单告诉我们。我们会在 1-3 个工作日内回复。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px]">
          {/* 表单 */}
          <div className="rounded-lg border border-background-200/60 bg-background-100 p-6 md:p-8">
            {formSuccess ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-3xl text-background-950">
                  <i className="ri-check-line"></i>
                </span>
                <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950">来信已收到</h2>
                <p className="mt-2 max-w-sm text-sm leading-7 text-foreground-600">
                  感谢你的反馈！我们会尽快阅读你的来信，并在 1-3 个工作日内回复。
                </p>
                <button
                  type="button"
                  onClick={() => setFormSuccess(false)}
                  className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-5 py-2.5 text-sm text-foreground-700 hover:text-foreground-950"
                >
                  <i className="ri-add-line"></i> 再写一封
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form="true"
                onSubmit={handleSubmit}
                className="grid grid-cols-1 gap-5 sm:grid-cols-2"
                noValidate
              >
                <div>
                  <label htmlFor="name" className="mb-2 block text-sm text-foreground-700">您的称呼</label>
                  <input id="name" name="name" type="text" required maxLength={50} placeholder="怎么称呼您" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm text-foreground-700">邮箱地址</label>
                  <input id="email" name="email" type="email" required placeholder="name@example.com" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="subject" className="mb-2 block text-sm text-foreground-700">来信主题</label>
                  <select id="subject" name="subject" required className={inputCls} defaultValue="阅读反馈">
                    <option value="阅读反馈">阅读反馈</option>
                    <option value="作品推荐">作品推荐 / 收录建议</option>
                    <option value="合作洽谈">投稿与商务合作</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="message" className="mb-2 block text-sm text-foreground-700">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={6}
                    placeholder="请详细描述你的问题或建议（500 字以内）"
                    className={`${inputCls} resize-none`}
                  ></textarea>
                </div>

                {/* 反垃圾辅助字段 */}
                <div className="form-aux-field">
                  <label htmlFor="website_alt">网站地址</label>
                  <input id="website_alt" name="website_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                {formError && (
                  <p className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400 sm:col-span-2" role="alert">
                    <i className="ri-error-warning-line"></i> {formError}
                  </p>
                )}

                <div className="flex items-center justify-between sm:col-span-2">
                  <p className="text-xs text-foreground-500">我们承诺不向第三方泄露你的信息</p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i> 提交中…
                      </>
                    ) : (
                      <>
                        发送来信 <i className="ri-send-plane-line"></i>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* 联系信息 */}
          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200/60 bg-background-100 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-mail-line text-primary-500"></i> 直接写信
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                也可直接发送邮件至我们的邮箱，主题请注明「反馈」或「合作」。
              </p>
              <a href="mailto:hello@chuixue-manga.example.com" className="mt-3 inline-block text-sm font-semibold text-primary-500 hover:text-primary-400">
                hello@chuixue-manga.example.com
              </a>
            </div>

            <div className="rounded-lg border border-background-200/60 bg-background-100 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-time-line text-accent-500"></i> 回复时间
              </h2>
              <ul className="mt-3 space-y-2 text-sm text-foreground-600">
                <li>工作日来信：24 小时内回复</li>
                <li>周末来信：顺延至下个工作日</li>
                <li>合作洽谈：1-3 个工作日回复</li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200/60 bg-background-100 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-question-line text-secondary-500"></i> 先看看常见问题
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                关于在线阅读、作品分类与更新的问题，或许在常见问题里已有答案。
              </p>
              <Link to="/faq" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-500 hover:text-primary-400">
                前往常见问题 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}